import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Session, SessionDraft, SessionSource } from '../models/session.model';
import { SESSIONS_SEED } from '../data/sessions.seed';
import { Participant, SessionGroup, SplitType } from '../../invitations/models/invitation.model';
import { InvitationMockService } from '../../invitations/services/invitation-mock.service';
import { OrgMockService } from '../../../service/org-mock.service';
import { composeSessionPool } from './session-pool';
import { runSplit } from './session-split';
import { PoolRow } from '../../reports/services/report-pool';

// v3: SplitType reduced to similar | diverse_exact — cached sessions carrying one of the three
// removed types would render a blank badge and fail a re-split (v2: optional expiry; v1: initial)
const STORAGE_KEY = 'fs_mock_sessions_v3';
const COUNTER_KEY = 'fs_mock_sessions_counter_v1';
// `v === undefined` is a required guard, not defensive filler: JSON.stringify(undefined) returns
// the value `undefined` (not a string), and JSON.parse coerces that to the string "undefined",
// which throws a SyntaxError. getById on a missing/removed id hits this on every call otherwise —
// this is inherited from ReportMockService's clone(), which has the same latent bug.
const clone = <T>(v: T): T => (v === undefined ? v : JSON.parse(JSON.stringify(v)));

/**
 * ONE representation of "no expiry": `undefined`. Empty string is folded into it here so that
 * every writer (wizard, board modal, in-invitation builder) stores the same thing — otherwise
 * `''` and `undefined` would both mean "never expires" and every reader would need two checks.
 * `undefined` also drops out of JSON.stringify, so the persisted row simply has no `expiresAt`.
 */
const normalizeExpiry = (v: string | undefined): string | undefined => (v ? v : undefined);

@Injectable({ providedIn: 'root' })
export class SessionMockService {
  private subject = new BehaviorSubject<Session[]>([]);
  private idCounter = 1;

  /**
   * `invitations` is injected because a session's participants live in the invitations store —
   * Sessions owns board/split state, not people. `orgs` is kept for parity with ReportMockService
   * (org display names) even though `primaryName` currently resolves off the invitations list.
   */
  constructor(private invitations: InvitationMockService, private orgs: OrgMockService) {
    const saved = localStorage.getItem(STORAGE_KEY);
    const list: Session[] = saved ? JSON.parse(saved) : clone(SESSIONS_SEED);
    this.subject.next(list);
    const mark = +(localStorage.getItem(COUNTER_KEY) ?? '1');
    // Same corruption guard as ReportMockService: isInteger (not isFinite) blocks NaN poisoning
    // idCounter forever, and blocks a fractional mark minting 'ses-4.7'.
    const usable = Number.isInteger(mark) && mark > 0;
    this.idCounter = usable ? Math.max(this.nextCounter(list), mark) : this.nextCounter(list);
  }

  /** Restore the seed (demos / tests). */
  reset(): void {
    const seed = clone(SESSIONS_SEED);
    this.commit(seed);
    this.idCounter = this.nextCounter(seed);
    localStorage.removeItem(COUNTER_KEY);
  }

  list(): Observable<Session[]> { return of(clone(this.subject.value)); }

  getById(id: string): Observable<Session | undefined> {
    return of(clone(this.subject.value.find(s => s.id === id)));
  }

  /**
   * Expiry is OPTIONAL: a group with no `expiresAt` never expires. The absence is checked
   * explicitly rather than left to `new Date(undefined)` — that yields NaN, and every NaN
   * comparison is false, so it would read as "not expired" only by accident and would break
   * the moment the comparison flipped direction.
   */
  isExpired(s: Session): boolean {
    if (!s.expiresAt) { return false; }
    return new Date(s.expiresAt).getTime() < Date.now();
  }

  /** The one Activity Group based on this invitation, if any (most recent wins). */
  forInvitation(invId: string): Observable<Session | undefined> {
    const hit = [...this.subject.value].reverse().find(s => s.primaryInvitationId === invId);
    return of(hit ? clone(hit) : undefined);
  }

  /**
   * Quick-create from an invitation: primary = that invitation, participants = its eligible
   * (scored AND unlocked) people, groups built by runSplit. One per invitation — any existing
   * invitation-primary group is removed first (re-create replaces).
   */
  createForInvitation(
    invId: string,
    req: { name: string; expiresAt?: string; splitType: SplitType; groupCount: number },
  ): Observable<Session> {
    // getById emits synchronously through of(...), so this resolves inline (same trick primaryName() uses).
    let eligible: Participant[] = [];
    this.invitations.getById(invId).subscribe(inv => { eligible = inv ? this.invitations.eligibleParticipants(inv) : []; }).unsubscribe();
    const kept = this.subject.value.filter(s => s.primaryInvitationId !== invId);
    this.commit(kept);                                               // drop the old one first
    return this.create({
      name: req.name,
      primaryInvitationId: invId,
      sources: [{ kind: 'invitation', id: invId }],
      participantIds: eligible.map(p => p.id),
      splitType: req.splitType,
      groupCount: req.groupCount,
      expiresAt: normalizeExpiry(req.expiresAt),
    });
  }

  /**
   * Every eligible participant across the chosen sources (orgs or invitations). The eligibility
   * rule is HANDED IN from InvitationMockService rather than restated — one definition of who may
   * appear in a session, shared with Reports.
   */
  pool(sources: SessionSource[]): Observable<PoolRow[]> {
    return this.invitations.list({ archived: false }).pipe(
      map(res => composeSessionPool(res.rows, sources, inv => this.invitations.eligibleParticipants(inv))));
  }

  create(draft: SessionDraft): Observable<Session> {
    const now = this.stamp();
    const session: Session = {
      id: this.newId(),
      name: draft.name.trim(),
      primaryInvitationId: draft.primaryInvitationId,
      primaryInvitationName: this.primaryName(draft.primaryInvitationId),
      sources: clone(draft.sources),
      participantIds: [...draft.participantIds],
      splitType: draft.splitType,
      groups: this.buildGroups(draft),
      expiresAt: normalizeExpiry(draft.expiresAt),
      createdBy: this.invitations.currentUser().name,
      createdAt: now, updatedAt: now,
    };
    this.commit([...this.subject.value, session]);
    return of(clone(session));
  }

  update(id: string, patch: Partial<SessionDraft>): Observable<Session> {
    return this.mutate(id, s => {
      if (patch.name !== undefined) { s.name = patch.name.trim(); }
      // `in` rather than truthiness: expiry is optional, so a caller that hands in an explicitly
      // cleared value must be able to REMOVE an existing one. A truthiness test would silently
      // keep the old date and make "clear the expiry" impossible.
      if ('expiresAt' in patch) { s.expiresAt = normalizeExpiry(patch.expiresAt); }
      if (patch.sources) { s.sources = clone(patch.sources); }
      if (patch.primaryInvitationId) {
        s.primaryInvitationId = patch.primaryInvitationId;
        s.primaryInvitationName = this.primaryName(patch.primaryInvitationId);
      }
      // Re-run the split only when membership or split settings changed.
      if (patch.participantIds || patch.splitType || patch.groupCount !== undefined) {
        if (patch.participantIds) { s.participantIds = [...patch.participantIds]; }
        if (patch.splitType) { s.splitType = patch.splitType; }
        s.groups = this.buildGroups({
          participantIds: s.participantIds, splitType: s.splitType,
          groupCount: patch.groupCount ?? s.groups.length,
        } as SessionDraft);
      }
      s.updatedAt = this.stamp();
    });
  }

  /**
   * Clone a definition under a new id. Arrays are copied, not aliased: two sessions sharing one
   * groups array would edit each other's board.
   */
  duplicate(id: string): Observable<Session> {
    const src = this.subject.value.find(s => s.id === id);
    if (!src) { throw new Error(`No session ${id}`); }
    const now = this.stamp();
    const copy: Session = {
      ...clone(src), id: this.newId(), name: `${src.name} copy`,
      createdBy: this.invitations.currentUser().name, createdAt: now, updatedAt: now,
    };
    this.commit([...this.subject.value, copy]);
    return of(clone(copy));
  }

  remove(id: string): Observable<void> {
    this.commit(this.subject.value.filter(s => s.id !== id));
    return of(void 0);
  }

  // --- board lifecycle ---

  resplit(id: string, req: { splitType: SplitType; groupCount: number }): Observable<Session> {
    return this.mutate(id, s => {
      s.splitType = req.splitType;
      s.groups = this.buildGroups({ participantIds: s.participantIds, splitType: req.splitType, groupCount: req.groupCount } as SessionDraft);
      s.updatedAt = this.stamp();
    });
  }

  addGroup(id: string): Observable<Session> {
    return this.mutate(id, s => {
      // High-water mark over SURVIVING group ids, not the array length: after a non-terminal
      // deleteGroup the array shrinks but ids don't renumber ([g-1,g-2,g-3] -> delete g-2 ->
      // [g-1,g-3], length 2), so `length + 1` would mint a second 'g-3' — a duplicate id that
      // breaks moveMember's `.find()` and any `trackBy: group.id`.
      const next = s.groups.reduce((m, g) => {
        const x = /^g-(\d+)$/.exec(g.id);
        return x ? Math.max(m, +x[1]) : m;
      }, 0) + 1;
      s.groups.push({ id: `g-${next}`, name: `Group ${next}`, memberIds: [] });
      s.updatedAt = this.stamp();
    });
  }

  renameGroup(id: string, groupId: string, name: string): Observable<Session> {
    return this.mutate(id, s => {
      const g = s.groups.find(x => x.id === groupId);
      if (g) { g.name = name.trim() || g.name; }
      s.updatedAt = this.stamp();
    });
  }

  deleteGroup(id: string, groupId: string): Observable<Session> {
    return this.mutate(id, s => {
      s.groups = s.groups.filter(g => g.id !== groupId);
      s.updatedAt = this.stamp();
    });
  }

  moveMember(id: string, memberId: string, toGroupId: string, toIndex: number): Observable<Session> {
    return this.mutate(id, s => {
      s.groups.forEach(g => { g.memberIds = g.memberIds.filter(m => m !== memberId); });
      const dest = s.groups.find(g => g.id === toGroupId);
      if (dest) { dest.memberIds.splice(Math.max(0, Math.min(toIndex, dest.memberIds.length)), 0, memberId); }
      s.updatedAt = this.stamp();
    });
  }

  // --- internals ---

  private buildGroups(d: Pick<SessionDraft, 'participantIds' | 'splitType' | 'groupCount'>): SessionGroup[] {
    const members = this.invitations.participantsByIds(d.participantIds);
    return runSplit(members, d.splitType, d.groupCount);
  }

  private primaryName(invId: string): string {
    let name = '';
    this.invitations.list({ archived: false }).subscribe(res => {
      name = res.rows.find(i => i.id === invId)?.name ?? '';
    });
    return name;
  }

  private mutate(id: string, fn: (s: Session) => void): Observable<Session> {
    const list = clone(this.subject.value);
    const target = list.find(s => s.id === id);
    if (!target) { throw new Error(`No session ${id}`); }
    fn(target);
    this.commit(list);
    return of(clone(target));
  }

  private commit(list: Session[]): void {
    this.subject.next(list);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  /**
   * Ids count UP from the highest ever seen — never `length + 1`, which re-issues the id of a
   * deleted session and collides with anything still holding it.
   */
  private newId(): string {
    const id = `ses-${this.idCounter++}`;
    localStorage.setItem(COUNTER_KEY, String(this.idCounter));
    return id;
  }

  private nextCounter(list: Session[]): number {
    return list.reduce((max, s) => {
      const m = /^ses-(\d+)$/.exec(s.id);
      return m ? Math.max(max, +m[1] + 1) : max;
    }, 1);
  }

  /** Wall-clock is fine for mutations (unlike seeds, which must stay deterministic). */
  private stamp(): string { return new Date().toISOString(); }
}
